// /home/projetocrm/capta-crm-web/src/LayoutComMenu.jsx
import React, { useCallback, useMemo, useState, Suspense, lazy } from "react";
import Layout from "./Components/Layout";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import { useEmpresa } from "./context/EmpresaContext";
import { useAuth } from "./context/AuthContext";
import RotaPrivada from "./Components/RotaPrivada";
import CaptaChatWidget from "./utils/CaptaChatWidget";

import {
  Home,
  Users,
  UserRound,
  CreditCard,
  Settings,
  FileText,
  ChevronDown,
  ChevronUp,
  LogOut,
  BarChart3,
  ShieldCheck,
  Menu as MenuIcon,
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  MessageSquare,
  Zap,
  Activity,
  BadgePercent,
  CalendarDays,
  Building2,
  Megaphone,
  Stethoscope,
  ClipboardList,
  Target,
  MapPin,
} from "lucide-react";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import logoCapta from "./assets/logo-capta.png";

const TestRecebimento = lazy(() => import("./pages/TestRecebimento"));
const FluxoCaixa = lazy(() => import("./pages/FluxoCaixa"));
const Usuarios = lazy(() => import("./pages/Usuarios"));
const CondicoesPagamento = lazy(() => import("./pages/CondicoesPagamento"));
const RelatorioFluxoCaixa = lazy(() => import("./pages/RelatorioFluxoCaixa"));
const DashboardPrincipal = lazy(() => import("./pages/DashboardPrincipal"));
const Permissoes = lazy(() => import("./pages/Permissoes"));
const DashboardVendedores = lazy(
  () => import("./Components/dashboard/DashboardVendedores"),
);
const PainelAdministrador = lazy(
  () => import("./Components/dashboard/DashboardAdministrador"),
);
const DentistaDashboard = lazy(
  () => import("./Components/dashboard/DentistaDashboard"),
);
const RelatorioVendasCaptador = lazy(
  () => import("./pages/relatorios/RelatorioVendasCaptador"),
);
const MensagemRapidaAdmin = lazy(
  () => import("./Components/MensagemRapidaAdmin"),
);
const ConexaoZap = lazy(() => import("./pages/configuracoes/ConexaoZap"));
const Empresas = lazy(() => import("./pages/Empresas"));
const CaptaZap = lazy(() => import("./modules/captazap/CaptaZap"));
const CaptaZapDepartamentos = lazy(
  () => import("./modules/captazap/pages/CaptaZapDepartamentos"),
);
const FunilAnunciosSupervisao = lazy(
  () => import("./modules/captazap/components/FunilAnunciosSupervisao"),
);
const DraMaluReguaConfiguravel = lazy(
  () => import("./modules/captazap/components/DraMaluReguaConfiguravel"),
);
const MarketingDashboard = lazy(
  () => import("./Components/dashboard/MarketingDashboard"),
);
const PainelEstrategicoComercial = lazy(
  () => import("./Components/dashboard/PainelEstrategicoComercial"),
);
const CaptaOneCentralAoVivo = lazy(
  () => import("./pages/CaptaOneCentralAoVivo"),
);
const MedidorTelemetria = lazy(
  () => import("./Components/dashboard/Medidor_Telemetria"),
);
const LeadsV2 = lazy(() => import("./pages/Leadsv2.jsx"));
const ConfiguracaoComissoes = lazy(
  () => import("./pages/configuracoes/comissoes/ConfiguracaoComissoes"),
);
const Feriados = lazy(() => import("./pages/configuracoes/feriados/Feriados"));
const PainelComissionamento = lazy(
  () => import("./pages/relatorios/PainelComissionamento"),
);
const TransferenciaLeadAprovacao = lazy(
  () => import("./Components/TransferenciaLeadAprovacao"),
);

const NavItem = React.memo(function NavItem({
  to,
  icon,
  children,
  aberto,
  onClick,
}) {
  return (
    <Link
      to={to}
      className="flex items-start gap-2 px-4 py-2 rounded-lg hover:bg-blue-700 transition"
      onClick={onClick}
    >
      <span className="shrink-0 mt-0.5">{icon}</span>
      {aberto && (
        <span className="min-w-0 whitespace-normal break-normal leading-tight text-left">
          {children}
        </span>
      )}
    </Link>
  );
});

const Separator = React.memo(function Separator() {
  return <hr className="my-2 border-t border-blue-400 opacity-30" />;
});

// Item exclusivo do bloco Cadastro: mais compacto, com identificação visual
// da rota ativa. Não altera nenhuma regra de permissão ou navegação.
const CadastroNavItem = React.memo(function CadastroNavItem({
  to,
  icon,
  children,
  onClick,
}) {
  const location = useLocation();
  const ativo =
    location.pathname === to ||
    (to !== "/" && location.pathname.startsWith(`${to}/`));

  return (
    <Link
      to={to}
      onClick={onClick}
      aria-current={ativo ? "page" : undefined}
      className={`group relative flex min-h-9 items-center gap-2.5 rounded-lg border px-3 py-1.5 text-[13px] font-medium transition-all duration-150 ${
        ativo
          ? "border-white/15 bg-white/15 text-white shadow-sm"
          : "border-transparent text-blue-50/90 hover:bg-white/10 hover:text-white"
      }`}
    >
      {ativo && (
        <span
          className="absolute inset-y-1.5 left-0 w-[3px] rounded-r-full bg-sky-300"
          aria-hidden="true"
        />
      )}

      <span
        className={`grid h-6 w-6 shrink-0 place-items-center rounded-md transition ${
          ativo
            ? "bg-white/15 text-white"
            : "bg-white/[0.07] text-blue-100 group-hover:bg-white/10"
        }`}
      >
        {icon}
      </span>

      <span className="min-w-0 truncate">{children}</span>
    </Link>
  );
});

// Item visual para cadastros que serão implementados nas próximas etapas.
// Não possui rota e não altera permissões; serve apenas para manter a
// arquitetura final do menu visível durante a construção.
const CadastroFutureItem = React.memo(function CadastroFutureItem({
  icon,
  children,
}) {
  return (
    <div
      aria-disabled="true"
      title="Cadastro será habilitado na próxima etapa"
      className="flex min-h-9 cursor-default items-center gap-2.5 rounded-lg border border-transparent px-3 py-1.5 text-[13px] font-medium text-blue-100/65"
    >
      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-md bg-white/[0.05] text-blue-100/70">
        {icon}
      </span>
      <span className="min-w-0 truncate">{children}</span>
    </div>
  );
});

// Separação apenas visual entre conjuntos do Cadastro. Sem títulos extras
// para manter o menu leve e evitar poluição visual.
const CadastroDivider = React.memo(function CadastroDivider() {
  return <div className="mx-3 my-1.5 h-px bg-white/10" aria-hidden="true" />;
});

const SemPermissao = React.memo(function SemPermissao() {
  return <h2>Sem permissão</h2>;
});

const TelaCarregando = React.memo(function TelaCarregando() {
  return (
    <Layout>
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-l from-blue-600 via-blue-700 to-blue-900 text-white">
        <div className="text-lg font-semibold">Carregando...</div>
      </div>
    </Layout>
  );
});

const PERMISSOES_LAYOUT_ALIASES = {
  "captazap.menu": [
    "captazap",
    "captazap.acessar",
    "captazap.access",
    "captazap_menu",
    "menu.captazap",
    "menu_captazap",
    "whatsapp.menu",
    "whatsapp.captazap",
  ],
  "captazap.visualizar": [
    "captazap.ver",
    "captazap.listar",
    "captazap.tickets.visualizar",
    "captazap.chamados.visualizar",
    "whatsapp.visualizar",
    "whatsapp.ver",
  ],
  "captazap.supervisionar_equipe": [
    "captazap.ver_equipe",
    "captazap.ver_minhas_equipe",
    "captazap.minhas_equipe",
    "captazap.minhas_equipe.visualizar",
    "captazap.equipe",
    "captazap.equipe.visualizar",
    "captazap.atendimentos_equipe",
    "captazap.supervisao",
    "captazap.supervisao.visualizar",
    "captazap.supervisao.acessar",
    "captazap.supervisor",
    "captazap.supervisor.visualizar",
    "captazap.supervisor.acessar",
    "captazap.supervisionar",
    "atendimentos.ver_equipe",
    "ver_atendimentos_equipe",
    "visao_equipe_captazap",
    "supervisao.captazap",
    "supervisao.captazap.visualizar",
    "supervisao_captazap",
    "menu.captazap.supervisao",
    "menu.supervisao_captazap",
    "whatsapp.ver_equipe",
    "whatsapp.supervisionar_equipe",
    "whatsapp.supervisao",
    "whatsapp.supervisao.visualizar",
  ],
  "captazap.departamentos": [
    "captazap.filas",
    "captazap.departamento",
    "captazap.filas_departamentos",
    "captazap.departamentos.gerenciar",
    "whatsapp.departamentos",
  ],
  "mensagem_rapida.visualizar": [
    "mensagem_rapida",
    "mensagem_rapida.acessar",
    "mensagem_rapida.listar",
    "mensagem_rapida.ver",
    "mensagem_rapida.gerenciar",
    "mensagem_rapida.admin",
    "mensagens_rapidas",
    "mensagens_rapidas.acessar",
    "mensagens_rapidas.visualizar",
    "mensagens_rapidas.listar",
    "mensagens_rapidas.ver",
    "mensagens_rapidas.gerenciar",
    "mensagens_rapidas.admin",
    "menu.mensagens_rapidas",
    "menu.mensagem_rapida",
    "captazap.mensagens_rapidas",
    "captazap.mensagem_rapida",
    "whatsapp.mensagens_rapidas",
    "whatsapp.mensagem_rapida",
  ],
};

function normalizarTextoPermissaoLayout(valor = "") {
  return String(valor || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function normalizarValorPermissaoLayout(valor) {
  if (valor === true || valor === 1) return true;
  if (valor === false || valor === 0 || valor == null) return false;

  const texto = normalizarTextoPermissaoLayout(valor);
  return ["true", "1", "sim", "s", "yes", "y", "ativo", "permitido"].includes(
    texto,
  );
}

function normalizarMapaPermissoesLayout(raw) {
  if (!raw) return {};

  let value = raw;

  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return {};
    }
  }

  if (value && typeof value === "object" && "permissoes" in value) {
    return normalizarMapaPermissoesLayout(value.permissoes);
  }

  const mapa = {};

  if (Array.isArray(value)) {
    for (const item of value) {
      if (typeof item === "string") {
        mapa[item] = true;
        mapa[normalizarTextoPermissaoLayout(item)] = true;
      } else if (item && typeof item === "object") {
        const chave =
          item.chave ||
          item.permissao ||
          item.permission ||
          item.nome ||
          item.name ||
          "";
        if (chave) {
          const permitido =
            item.valor ??
            item.ativo ??
            item.permitido ??
            item.enabled ??
            item.checked ??
            true;
          mapa[chave] = normalizarValorPermissaoLayout(permitido);
          mapa[normalizarTextoPermissaoLayout(chave)] =
            normalizarValorPermissaoLayout(permitido);
        }
      }
    }

    return mapa;
  }

  if (typeof value !== "object") return {};

  for (const [chave, permitido] of Object.entries(value)) {
    // Quando o backend manda permissões por empresa, estes valores podem ser
    // objetos aninhados. O resolver adiciona esses objetos separadamente; aqui
    // ignoramos como permissão direta para não transformar "2" em permissão.
    if (permitido && typeof permitido === "object") continue;

    mapa[chave] = normalizarValorPermissaoLayout(permitido);
    mapa[normalizarTextoPermissaoLayout(chave)] =
      normalizarValorPermissaoLayout(permitido);
  }

  return mapa;
}

function resolverPermissoesUsuarioEmpresaLayout(usuario = {}, empresaId = "") {
  const empresaKey = String(empresaId || "");
  const permissoesUsuario = usuario?.permissoes || {};

  const candidatos = [
    permissoesUsuario?.[empresaKey],
    permissoesUsuario?.[Number(empresaKey)],
    usuario?.permissoes_empresa?.[empresaKey],
    usuario?.permissoesEmpresa?.[empresaKey],
    usuario?.permissoes_por_empresa?.[empresaKey],
    usuario?.permissoesPorEmpresa?.[empresaKey],
    usuario?.permissoes_empresa,
    usuario?.permissoesEmpresa,
    usuario?.permissoes_por_empresa,
    usuario?.permissoesPorEmpresa,
    permissoesUsuario,
  ];

  return candidatos.reduce(
    (acc, candidato) => ({
      ...acc,
      ...normalizarMapaPermissoesLayout(candidato),
    }),
    {},
  );
}

function isUsuarioAdministradorSistemaLayout(usuario = {}) {
  const textos = [
    usuario?.cargo,
    usuario?.perfil,
    usuario?.role,
    usuario?.tipo,
    usuario?.tipo_usuario,
    usuario?.nivel_acesso,
    usuario?.nome_perfil,
    usuario?.perfil_nome,
    usuario?.grupo,
  ]
    .map(normalizarTextoPermissaoLayout)
    .filter(Boolean);

  const flags = [
    usuario?.admin,
    usuario?.is_admin,
    usuario?.isAdmin,
    usuario?.administrador,
    usuario?.is_administrador,
    usuario?.super_admin,
    usuario?.superAdmin,
  ];

  if (flags.some((flag) => normalizarValorPermissaoLayout(flag))) return true;

  return textos.some((texto) =>
    ["admin", "administrador", "administradora", "superadmin", "root"].includes(
      texto,
    ),
  );
}

function getChavesPermissaoLayout(chave) {
  return [chave, ...(PERMISSOES_LAYOUT_ALIASES[chave] || [])];
}

const SidebarContent = React.memo(function SidebarContent({
  aberto,
  closeMobile,
  openRelatorios,
  setOpenRelatorios,
  openCadastro,
  setOpenCadastro,
  openOperacoes,
  setOpenOperacoes,
  openCaptaZap,
  setOpenCaptaZap,
  openDashboards,
  setOpenDashboards,
  openConfiguracoes,
  setOpenConfiguracoes,
  perms,
  empresaAtivaEfetiva,
}) {
  const {
    podeVerFluxo,
    podeVerLeadsSpeed,
    podeCadastrarUsuarios,
    podeVerPermissoes,
    podeVerMenuRelatorios,
    podeVerRelatorioFluxoCaixa,
    podeVerPainelDentista,
    podeVerPainelAdmin,
    podeVerPainelVendedor,
    podeVerMenuCaptaZap,
    podeVerCaptaZap,
    podeSupervisionarCaptaZap,
    podeGerenciarDepartamentosCaptaZap,
    podeVerPainelMarketing,
    podeVerMedidor,
    podeConfigurarComissoes,
    podeAprovarTransferenciasLead,
    podeGerenciarMensagensRapidas,
  } = perms;

  const nomeEmpresaAtual = useMemo(() => {
    if (!empresaAtivaEfetiva) return "";
    return (
      empresaAtivaEfetiva.nome_fantasia ||
      empresaAtivaEfetiva.nome ||
      empresaAtivaEfetiva.razao_social ||
      `Empresa ${empresaAtivaEfetiva.id}`
    );
  }, [empresaAtivaEfetiva]);

  const ConfiguracoesSubMenu = useMemo(
    () => (
      <div className="pl-6 mt-2 space-y-1">
        {podeConfigurarComissoes && (
          <>
            <NavItem
              to="/configuracoes/comissoes"
              icon={<Settings size={18} />}
              aberto={aberto}
              onClick={closeMobile}
            >
              Comissões
            </NavItem>

            <NavItem
              to="/configuracoes/feriados"
              icon={<CalendarDays size={18} />}
              aberto={aberto}
              onClick={closeMobile}
            >
              Feriados
            </NavItem>
          </>
        )}

        {podeCadastrarUsuarios && (
          <NavItem
            to="/conexaozap"
            icon={<Zap size={18} />}
            aberto={aberto}
            onClick={closeMobile}
          >
            Conexão Z-API
          </NavItem>
        )}

        {podeVerMedidor && (
          <NavItem
            to="/configuracoes/medidor-telemetria"
            icon={<Activity size={18} />}
            aberto={aberto}
            onClick={closeMobile}
          >
            Telemetria
          </NavItem>
        )}
      </div>
    ),
    [
      aberto,
      closeMobile,
      podeCadastrarUsuarios,
      podeVerMedidor,
      podeConfigurarComissoes,
    ],
  );

  const mostrarMenuOperacoes =
    podeVerLeadsSpeed || podeVerFluxo || podeAprovarTransferenciasLead;

  return (
    <>
      <div className="flex items-center justify-center mb-4 transition-all duration-300">
        <Link to="/" className="flex items-center justify-center w-full">
          <img
            src={logoCapta}
            alt="CaptaCRM"
            className={`transition-all duration-300 drop-shadow-md ${
              aberto ? "w-32" : "w-10"
            }`}
          />
        </Link>
      </div>

      {aberto && (
        <div
          className="mb-3 rounded-2xl border border-white/15 bg-white/10 backdrop-blur-sm px-3 py-3 shadow-lg"
          title={nomeEmpresaAtual}
        >
          <div className="flex items-center gap-2">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(74,222,128,0.7)]" />
            <span className="text-[11px] text-blue-100 font-medium">
              Você está online em
            </span>
          </div>

          <div className="mt-1 text-sm font-bold text-white leading-snug whitespace-normal break-normal">
            {nomeEmpresaAtual || "Empresa não identificada"}
          </div>
        </div>
      )}

      <Separator />

      <NavItem
        to="/dashboard"
        icon={<Home size={20} />}
        aberto={aberto}
        onClick={closeMobile}
      >
        Início
      </NavItem>

      {(podeVerPainelAdmin ||
        podeVerPainelVendedor ||
        podeVerPainelDentista ||
        podeVerPainelMarketing) && (
        <>
          <button
            onClick={() => setOpenDashboards((v) => !v)}
            className="flex items-center justify-between w-full px-4 py-2 rounded-lg hover:bg-blue-800 transition"
            type="button"
          >
            <span className="flex items-center gap-2 min-w-0">
              <LayoutDashboard size={20} className="shrink-0" />
              {aberto && (
                <span className="min-w-0 whitespace-normal break-normal leading-tight text-left">
                  Dashboards
                </span>
              )}
            </span>
            {aberto &&
              (openDashboards ? (
                <ChevronUp size={18} />
              ) : (
                <ChevronDown size={18} />
              ))}
          </button>

          {openDashboards && aberto && (
            <div className="pl-6 mt-2 space-y-1">
              {podeVerPainelAdmin && (
                <NavItem
                  to="/painel-admin"
                  icon={<ShieldCheck size={18} />}
                  aberto={aberto}
                  onClick={closeMobile}
                >
                  Painel Admin
                </NavItem>
              )}
              {podeVerPainelVendedor && (
                <NavItem
                  to="/dashboard-vendedor"
                  icon={<BarChart3 size={18} />}
                  aberto={aberto}
                  onClick={closeMobile}
                >
                  Painel Vendas
                </NavItem>
              )}
              {podeVerPainelDentista && (
                <NavItem
                  to="/painel-dentistas"
                  icon={<BarChart3 size={18} />}
                  aberto={aberto}
                  onClick={closeMobile}
                >
                  Painel Dentistas
                </NavItem>
              )}
              {podeVerPainelMarketing && (
                <>
                  <NavItem
                    to="/marketing"
                    icon={<BarChart3 size={18} />}
                    aberto={aberto}
                    onClick={closeMobile}
                  >
                    Painel Marketing
                  </NavItem>
                  <NavItem
                    to="/painel-estrategico-comercial"
                    icon={<Activity size={18} />}
                    aberto={aberto}
                    onClick={closeMobile}
                  >
                    Painel Estratégico Comercial
                  </NavItem>
                </>
              )}
            </div>
          )}

          <Separator />
        </>
      )}

      {(podeVerMenuCaptaZap || podeSupervisionarCaptaZap) && (
        <>
          <button
            onClick={() => setOpenCaptaZap((v) => !v)}
            className="flex items-center justify-between w-full px-4 py-2 rounded-lg hover:bg-blue-800 transition"
            type="button"
          >
            <span className="flex items-center gap-2 min-w-0">
              <Zap size={20} className="shrink-0" />
              {aberto && (
                <span className="min-w-0 whitespace-normal break-normal leading-tight text-left">
                  CaptaZap
                </span>
              )}
            </span>
            {aberto &&
              (openCaptaZap ? (
                <ChevronUp size={18} />
              ) : (
                <ChevronDown size={18} />
              ))}
          </button>

          {openCaptaZap && aberto && (
            <div className="pl-6 mt-2 space-y-1">
              {podeVerCaptaZap && (
                <NavItem
                  to="/captazap"
                  icon={<MessageSquare size={18} />}
                  aberto={aberto}
                  onClick={closeMobile}
                >
                  Atendimento
                </NavItem>
              )}

              {podeGerenciarMensagensRapidas && (
                <NavItem
                  to="/mensagens-rapidas"
                  icon={<MessageSquare size={18} />}
                  aberto={aberto}
                  onClick={closeMobile}
                >
                  Mensagens Rápidas
                </NavItem>
              )}

              {podeSupervisionarCaptaZap && (
                <NavItem
                  to="/captazap/supervisao"
                  icon={<Users size={18} />}
                  aberto={aberto}
                  onClick={closeMobile}
                >
                  Supervisão da equipe
                </NavItem>
              )}

              {podeSupervisionarCaptaZap && (
                <NavItem
                  to="/captazap/funil-anuncios"
                  icon={<Megaphone size={18} />}
                  aberto={aberto}
                  onClick={closeMobile}
                >
                  Funil de Anúncios
                </NavItem>
              )}

              {podeSupervisionarCaptaZap && (
                <NavItem
                  to="/captazap/dra-malu/regua"
                  icon={<Settings size={18} />}
                  aberto={aberto}
                  onClick={closeMobile}
                >
                  Régua Dra. Malu
                </NavItem>
              )}

              {podeGerenciarDepartamentosCaptaZap && (
                <NavItem
                  to="/captazap/departamentos"
                  icon={<Building2 size={18} />}
                  aberto={aberto}
                  onClick={closeMobile}
                >
                  Departamentos
                </NavItem>
              )}
            </div>
          )}

          <Separator />
        </>
      )}

      {mostrarMenuOperacoes && (
        <div>
          <button
            onClick={() => setOpenOperacoes((v) => !v)}
            className="flex items-center justify-between w-full px-4 py-2 rounded-lg hover:bg-blue-800 transition"
            type="button"
          >
            <span className="flex items-center gap-2 min-w-0">
              <BarChart3 size={20} className="shrink-0" />
              {aberto && (
                <span className="min-w-0 whitespace-normal break-normal leading-tight text-left">
                  Operações
                </span>
              )}
            </span>
            {aberto &&
              (openOperacoes ? (
                <ChevronUp size={18} />
              ) : (
                <ChevronDown size={18} />
              ))}
          </button>

          {openOperacoes && aberto && (
            <div className="pl-6 mt-2 space-y-1">
              {podeVerLeadsSpeed && (
                <NavItem
                  to="/leadsspeed"
                  icon={<Activity size={18} />}
                  aberto={aberto}
                  onClick={closeMobile}
                >
                  LeadsSpeed
                </NavItem>
              )}

              {podeVerFluxo && (
                <NavItem
                  to="/fluxo-caixa"
                  icon={<CreditCard size={18} />}
                  aberto={aberto}
                  onClick={closeMobile}
                >
                  Fluxo de Caixa
                </NavItem>
              )}

              {podeAprovarTransferenciasLead && (
                <NavItem
                  to="/transferencias/aprovacao"
                  icon={<ShieldCheck size={18} />}
                  aberto={aberto}
                  onClick={closeMobile}
                >
                  Aprovar Transferência
                </NavItem>
              )}
            </div>
          )}
        </div>
      )}

      <Separator />

      <div>
        <button
          onClick={() => setOpenCadastro((v) => !v)}
          className="flex items-center justify-between w-full px-4 py-2 rounded-lg hover:bg-blue-800 transition"
          type="button"
        >
          <span className="flex items-center gap-2 min-w-0">
            <Users size={20} className="shrink-0" />
            {aberto && (
              <span className="min-w-0 whitespace-normal break-normal leading-tight text-left">
                Cadastro
              </span>
            )}
          </span>
          {aberto &&
            (openCadastro ? (
              <ChevronUp size={18} />
            ) : (
              <ChevronDown size={18} />
            ))}
        </button>

        {openCadastro && aberto && (
          <div className="mt-1 pl-3 pr-1">
            {podeCadastrarUsuarios && (
              <>
                <CadastroNavItem
                  to="/usuarios"
                  icon={<UserRound size={15} />}
                  onClick={closeMobile}
                >
                  Usuários
                </CadastroNavItem>

                <CadastroFutureItem icon={<Stethoscope size={15} />}>
                  Profissionais
                </CadastroFutureItem>

                <CadastroDivider />

                <CadastroFutureItem icon={<ClipboardList size={15} />}>
                  Procedimentos
                </CadastroFutureItem>

                <CadastroFutureItem icon={<Megaphone size={15} />}>
                  Fontes de Lead
                </CadastroFutureItem>

                <CadastroFutureItem icon={<Target size={15} />}>
                  Campanhas
                </CadastroFutureItem>

                <CadastroNavItem
                  to="/condicoes"
                  icon={<CreditCard size={15} />}
                  onClick={closeMobile}
                >
                  Condições Pgto
                </CadastroNavItem>

                <CadastroDivider />

                <CadastroNavItem
                  to="/empresas"
                  icon={<Building2 size={15} />}
                  onClick={closeMobile}
                >
                  Empresas
                </CadastroNavItem>

                <CadastroFutureItem icon={<MapPin size={15} />}>
                  Regiões
                </CadastroFutureItem>
              </>
            )}

            {podeCadastrarUsuarios && podeVerPermissoes && <CadastroDivider />}

            {podeVerPermissoes && (
              <CadastroNavItem
                to="/permissoes"
                icon={<ShieldCheck size={15} />}
                onClick={closeMobile}
              >
                Permissões
              </CadastroNavItem>
            )}
          </div>
        )}
      </div>

      <Separator />

      {podeVerMenuRelatorios && (
        <>
          <button
            onClick={() => setOpenRelatorios((v) => !v)}
            className="flex items-center justify-between w-full px-4 py-2 rounded-lg hover:bg-blue-800 transition"
            type="button"
          >
            <span className="flex items-center gap-2 min-w-0">
              <FileText size={20} className="shrink-0" />
              {aberto && (
                <span className="min-w-0 whitespace-normal break-normal leading-tight text-left">
                  Relatórios
                </span>
              )}
            </span>
            {aberto &&
              (openRelatorios ? (
                <ChevronUp size={18} />
              ) : (
                <ChevronDown size={18} />
              ))}
          </button>

          {openRelatorios && aberto && (
            <div className="pl-6 mt-2 space-y-1">
              <NavItem
                to="/relatorios/comissionamento"
                icon={<BadgePercent size={18} />}
                aberto={aberto}
                onClick={closeMobile}
              >
                Comissionamento
              </NavItem>

              {podeVerRelatorioFluxoCaixa && (
                <NavItem
                  to="/relatorios/fluxo-caixa"
                  icon={<FileText size={18} />}
                  aberto={aberto}
                  onClick={closeMobile}
                >
                  Fluxo de Caixa
                </NavItem>
              )}

              <NavItem
                to="/relatorios/vendas-captador"
                icon={<FileText size={18} />}
                aberto={aberto}
                onClick={closeMobile}
              >
                Vendas Captador
              </NavItem>
            </div>
          )}

          <Separator />
        </>
      )}

      <div>
        <button
          onClick={() => setOpenConfiguracoes((v) => !v)}
          className="flex items-center justify-between w-full px-4 py-2 rounded-lg hover:bg-blue-800 transition"
          type="button"
        >
          <span className="flex items-center gap-2 min-w-0">
            <Settings size={20} className="shrink-0" />
            {aberto && (
              <span className="min-w-0 whitespace-normal break-normal leading-tight text-left">
                Configurações
              </span>
            )}
          </span>
          {aberto &&
            (openConfiguracoes ? (
              <ChevronUp size={18} />
            ) : (
              <ChevronDown size={18} />
            ))}
        </button>

        {openConfiguracoes && aberto && ConfiguracoesSubMenu}
      </div>

      <div className="mt-auto space-y-2">
        <Separator />
        <button
          onClick={perms.logout}
          className="flex items-center gap-2 px-4 py-2 w-full text-left rounded-lg hover:bg-red-600 transition text-white"
          aria-label="Sair"
          type="button"
        >
          <LogOut size={20} className="shrink-0" />
          {aberto && (
            <span className="min-w-0 whitespace-normal break-normal leading-tight text-left">
              Sair
            </span>
          )}
        </button>
      </div>
    </>
  );
});

const AppRoutes = React.memo(function AppRoutes({
  perms,
  pushNotification,
  recebimentos,
  captadores,
  leads,
  usuario,
  empresaAtivaEfetiva,
}) {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <RotaPrivada>
            <DashboardPrincipal />
          </RotaPrivada>
        }
      />
      <Route
        path="/dashboard"
        element={
          <RotaPrivada>
            <DashboardPrincipal />
          </RotaPrivada>
        }
      />
      <Route
        path="/dashboard-vendedor"
        element={
          <RotaPrivada>
            {perms.podeVerPainelVendedor ? (
              <DashboardVendedores />
            ) : (
              <SemPermissao />
            )}
          </RotaPrivada>
        }
      />
      <Route
        path="/painel-admin"
        element={
          <RotaPrivada>
            {perms.podeVerPainelAdmin ? (
              <PainelAdministrador empresaId={usuario?.empresa_id} />
            ) : (
              <SemPermissao />
            )}
          </RotaPrivada>
        }
      />
      <Route
        path="/painel-dentistas"
        element={
          <RotaPrivada>
            {perms.podeVerPainelDentista ? (
              <DentistaDashboard />
            ) : (
              <SemPermissao />
            )}
          </RotaPrivada>
        }
      />
      <Route
        path="/marketing"
        element={
          <RotaPrivada>
            {perms.podeVerPainelMarketing ? (
              <MarketingDashboard />
            ) : (
              <SemPermissao />
            )}
          </RotaPrivada>
        }
      />
      <Route
        path="/painel-estrategico-comercial"
        element={
          <RotaPrivada>
            {perms.podeVerPainelMarketing ? (
              <PainelEstrategicoComercial />
            ) : (
              <SemPermissao />
            )}
          </RotaPrivada>
        }
      />
      <Route
        path="/capta-one"
        element={
          <RotaPrivada>
            <CaptaOneCentralAoVivo />
          </RotaPrivada>
        }
      />
      <Route
        path="/usuarios"
        element={
          <RotaPrivada>
            {perms.podeCadastrarUsuarios ? <Usuarios /> : <SemPermissao />}
          </RotaPrivada>
        }
      />
      <Route
        path="/condicoes"
        element={
          <RotaPrivada>
            {perms.podeCadastrarUsuarios ? (
              <CondicoesPagamento />
            ) : (
              <SemPermissao />
            )}
          </RotaPrivada>
        }
      />
      <Route
        path="/leadsspeed"
        element={
          <RotaPrivada>
            {perms.podeVerLeadsSpeed ? <LeadsV2 /> : <SemPermissao />}
          </RotaPrivada>
        }
      />
      <Route
        path="/captazap"
        element={
          <RotaPrivada>
            {perms.podeVerCaptaZap ? <CaptaZap /> : <SemPermissao />}
          </RotaPrivada>
        }
      />
      <Route
        path="/captazap/supervisao"
        element={
          <RotaPrivada>
            {perms.podeSupervisionarCaptaZap ? (
              <CaptaZap abrirSupervisaoInicial />
            ) : (
              <SemPermissao />
            )}
          </RotaPrivada>
        }
      />
      <Route
        path="/captazap/funil-anuncios"
        element={
          <RotaPrivada>
            {perms.podeSupervisionarCaptaZap ? (
              <FunilAnunciosSupervisao
                empresaId={empresaAtivaEfetiva?.id || usuario?.empresa_id}
              />
            ) : (
              <SemPermissao />
            )}
          </RotaPrivada>
        }
      />
      <Route
        path="/captazap/dra-malu/regua"
        element={
          <RotaPrivada>
            {perms.podeSupervisionarCaptaZap ? (
              <DraMaluReguaConfiguravel
                empresaId={empresaAtivaEfetiva?.id || usuario?.empresa_id}
              />
            ) : (
              <SemPermissao />
            )}
          </RotaPrivada>
        }
      />
      <Route
        path="/captazap/departamentos"
        element={
          <RotaPrivada>
            {perms.podeGerenciarDepartamentosCaptaZap ? (
              <CaptaZapDepartamentos
                empresaId={empresaAtivaEfetiva?.id || usuario?.empresa_id}
                usuario={usuario}
              />
            ) : (
              <SemPermissao />
            )}
          </RotaPrivada>
        }
      />
      <Route
        path="/leads-v2"
        element={
          <RotaPrivada>
            {perms.podeVerLeadsSpeed ? <LeadsV2 /> : <SemPermissao />}
          </RotaPrivada>
        }
      />
      <Route
        path="/transferencias/aprovacao"
        element={
          <RotaPrivada>
            {perms.podeAprovarTransferenciasLead ? (
              <TransferenciaLeadAprovacao />
            ) : (
              <SemPermissao />
            )}
          </RotaPrivada>
        }
      />
      <Route
        path="/configuracoes"
        element={
          <RotaPrivada>
            <h2 className="text-2xl font-bold mb-4">⚙️ Configurações</h2>
            <p>Selecione uma opção no submenu.</p>
          </RotaPrivada>
        }
      />
      <Route
        path="/configuracoes/comissoes"
        element={
          <RotaPrivada>
            {perms.podeConfigurarComissoes ? (
              <ConfiguracaoComissoes />
            ) : (
              <SemPermissao />
            )}
          </RotaPrivada>
        }
      />
      <Route
        path="/configuracoes/feriados"
        element={
          <RotaPrivada>
            {perms.podeConfigurarComissoes ? <Feriados /> : <SemPermissao />}
          </RotaPrivada>
        }
      />
      <Route
        path="/relatorios/comissionamento"
        element={
          <RotaPrivada>
            {perms.podeVerMenuRelatorios ? (
              <PainelComissionamento />
            ) : (
              <SemPermissao />
            )}
          </RotaPrivada>
        }
      />
      <Route
        path="/configuracoes/medidor-telemetria"
        element={
          <RotaPrivada>
            {perms.podeVerMedidor ? <MedidorTelemetria /> : <SemPermissao />}
          </RotaPrivada>
        }
      />
      <Route
        path="/relatorios/fluxo-caixa"
        element={
          <RotaPrivada>
            {perms.podeVerMenuRelatorios && perms.podeVerRelatorioFluxoCaixa ? (
              <RelatorioFluxoCaixa />
            ) : (
              <SemPermissao />
            )}
          </RotaPrivada>
        }
      />
      <Route
        path="/relatorios/vendas-captador"
        element={
          <RotaPrivada>
            {perms.podeVerMenuRelatorios ? (
              <RelatorioVendasCaptador
                recebimentos={recebimentos}
                captadores={captadores}
                leads={leads}
              />
            ) : (
              <SemPermissao />
            )}
          </RotaPrivada>
        }
      />
      <Route
        path="/permissoes"
        element={
          <RotaPrivada>
            {perms.podeVerPermissoes ? <Permissoes /> : <SemPermissao />}
          </RotaPrivada>
        }
      />
      <Route
        path="/teste-recebimento"
        element={
          <RotaPrivada>
            <TestRecebimento />
          </RotaPrivada>
        }
      />
      <Route
        path="/mensagens-rapidas"
        element={
          <RotaPrivada>
            {perms.podeGerenciarMensagensRapidas ? (
              <MensagemRapidaAdmin
                empresaId={empresaAtivaEfetiva?.id || usuario?.empresa_id}
              />
            ) : (
              <SemPermissao />
            )}
          </RotaPrivada>
        }
      />
      <Route
        path="/conexaozap"
        element={
          <RotaPrivada>
            {perms.podeCadastrarUsuarios ? (
              <ConexaoZap empresaId={usuario?.empresa_id} />
            ) : (
              <SemPermissao />
            )}
          </RotaPrivada>
        }
      />
      <Route
        path="/empresas"
        element={
          <RotaPrivada>
            {perms.podeCadastrarUsuarios ? <Empresas /> : <SemPermissao />}
          </RotaPrivada>
        }
      />
      <Route
        path="/fluxo-caixa"
        element={
          <RotaPrivada>
            {perms.podeVerFluxo ? <FluxoCaixa /> : <SemPermissao />}
          </RotaPrivada>
        }
      />
    </Routes>
  );
});

export default function LayoutComMenu({
  pushNotification,
  recebimentos,
  captadores,
  leads,
}) {
  const location = useLocation();
  const isCaptaOneTv = location.pathname === "/capta-one";

  const { usuario, logout, carregando } = useAuth();
  const { empresaAtiva, trocarEmpresa } = useEmpresa();

  const [openRelatorios, setOpenRelatorios] = useState(false);
  const [openCadastro, setOpenCadastro] = useState(false);
  const [openOperacoes, setOpenOperacoes] = useState(false);
  const [openCaptaZap, setOpenCaptaZap] = useState(false);
  const [openDashboards, setOpenDashboards] = useState(false);
  const [openConfiguracoes, setOpenConfiguracoes] = useState(false);

  const [menuAberto, setMenuAberto] = useState(true);
  const [sidebarMobile, setSidebarMobile] = useState(false);

  const closeMobile = useCallback(() => setSidebarMobile(false), []);

  const empresaAtivaEfetiva = useMemo(() => {
    if (empresaAtiva?.id) return empresaAtiva;

    if (Array.isArray(usuario?.empresas) && usuario.empresas.length > 0) {
      return (
        usuario.empresas.find(
          (emp) => String(emp.id) === String(usuario?.empresa_id),
        ) || usuario.empresas[0]
      );
    }

    if (usuario?.empresa_id) {
      return { id: usuario.empresa_id };
    }

    return null;
  }, [empresaAtiva, usuario]);

  React.useEffect(() => {
    if (!empresaAtiva?.id && empresaAtivaEfetiva?.id) {
      trocarEmpresa(empresaAtivaEfetiva);
    }
  }, [empresaAtiva?.id, empresaAtivaEfetiva, trocarEmpresa]);

  const permissoesAtuais = useMemo(() => {
    return resolverPermissoesUsuarioEmpresaLayout(
      usuario,
      empresaAtivaEfetiva?.id,
    );
  }, [usuario, empresaAtivaEfetiva?.id]);

  const isAdmin = useMemo(
    () => isUsuarioAdministradorSistemaLayout(usuario),
    [usuario],
  );

  const temPermissao = useCallback(
    (chave) => {
      if (isAdmin) return true;

      return getChavesPermissaoLayout(chave).some((chavePossivel) => {
        const normalizada = normalizarTextoPermissaoLayout(chavePossivel);
        return Boolean(
          permissoesAtuais[chavePossivel] || permissoesAtuais[normalizada],
        );
      });
    },
    [isAdmin, permissoesAtuais],
  );

  const temAlgumaPermissao = useCallback(
    (chaves = []) => chaves.some((chave) => temPermissao(chave)),
    [temPermissao],
  );

  const perms = useMemo(() => {
    const podeVerFluxo = temPermissao("fluxo_caixa.visualizar");
    const podeVerLeadsSpeed = temPermissao("leadsspeed.visualizar");
    const podeCadastrarUsuarios = temPermissao("usuarios.cadastrar");
    const podeVerPermissoes = temPermissao("usuarios.cadastrar");
    const podeVerMenuRelatorios = temPermissao("relatorios.visualizar");
    const podeVerRelatorioFluxoCaixa = temPermissao("relatorios.fluxo_caixa");
    const podeVerPainelDentista = temPermissao("dashboard.painel_dentista");
    const podeVerPainelAdmin = temPermissao("dashboard.painel_administrador");
    const podeVerPainelVendedor = temPermissao("dashboard.painel_vendedor");
    const podeSupervisionarCaptaZap = temPermissao(
      "captazap.supervisionar_equipe",
    );
    const podeVerMenuCaptaZap =
      temPermissao("captazap.menu") || podeSupervisionarCaptaZap;
    const podeVerCaptaZap = temPermissao("captazap.visualizar");
    const podeGerenciarDepartamentosCaptaZap = temPermissao(
      "captazap.departamentos",
    );
    const podeVerPainelMarketing =
      temPermissao("dashboard.painel_marketing") ||
      temPermissao("relatorios.visualizar");
    const podeVerMedidor = isAdmin || temPermissao("telemetria.leads.medidor");
    const podeConfigurarComissoes = temPermissao("comissoes.configurar");
    const podeAprovarTransferenciasLead = temPermissao(
      "lead.transferencia.aprovar",
    );
    const podeGerenciarMensagensRapidas = temAlgumaPermissao([
      "mensagem_rapida.visualizar",
      "mensagem_rapida.gerenciar",
      "mensagens_rapidas.visualizar",
      "mensagens_rapidas.gerenciar",
      "menu.mensagens_rapidas",
    ]);

    return {
      podeVerFluxo,
      podeVerLeadsSpeed,
      podeCadastrarUsuarios,
      podeVerPermissoes,
      podeVerMenuRelatorios,
      podeVerRelatorioFluxoCaixa,
      podeVerPainelDentista,
      podeVerPainelAdmin,
      podeVerPainelVendedor,
      podeVerMenuCaptaZap,
      podeVerCaptaZap,
      podeSupervisionarCaptaZap,
      podeGerenciarDepartamentosCaptaZap,
      podeVerPainelMarketing,
      podeVerMedidor,
      podeConfigurarComissoes,
      podeAprovarTransferenciasLead,
      podeGerenciarMensagensRapidas,
      logout,
    };
  }, [temPermissao, temAlgumaPermissao, isAdmin, logout]);

  if (carregando) {
    return <TelaCarregando />;
  }

  if (!usuario) {
    return <TelaCarregando />;
  }

  if (!empresaAtivaEfetiva) {
    return <TelaCarregando />;
  }

  return (
    <Layout>
      <div className="flex min-h-screen w-full bg-gradient-to-l from-blue-600 via-blue-700 to-blue-900">
        {!isCaptaOneTv && (
          <aside
            className={`
              hidden lg:flex flex-col bg-gradient-to-r from-blue-600 via-blue-700 to-blue-900
              text-white shadow-2xl transition-all duration-300 z-40
              ${menuAberto ? "w-64 p-6 space-y-4" : "w-16 p-3 space-y-2"}
              fixed top-0 left-0 h-screen overflow-y-auto overflow-x-hidden overscroll-contain pb-6
              [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.35)_transparent]
              border-r-4 border-white drop-shadow-[4px_0px_10px_rgba(255,255,255,0.12)]
            `}
          >
            <button
              onClick={() => setMenuAberto((v) => !v)}
              className="text-white hover:text-blue-300 transition text-2xl focus:outline-none mb-2"
              aria-label="Abrir/fechar menu"
              type="button"
            >
              {menuAberto ? (
                <ChevronLeft size={28} />
              ) : (
                <ChevronRight size={28} />
              )}
            </button>

            <SidebarContent
              aberto={menuAberto}
              closeMobile={closeMobile}
              openRelatorios={openRelatorios}
              setOpenRelatorios={setOpenRelatorios}
              openCadastro={openCadastro}
              setOpenCadastro={setOpenCadastro}
              openOperacoes={openOperacoes}
              setOpenOperacoes={setOpenOperacoes}
              openCaptaZap={openCaptaZap}
              setOpenCaptaZap={setOpenCaptaZap}
              openDashboards={openDashboards}
              setOpenDashboards={setOpenDashboards}
              openConfiguracoes={openConfiguracoes}
              setOpenConfiguracoes={setOpenConfiguracoes}
              perms={perms}
              empresaAtivaEfetiva={empresaAtivaEfetiva}
            />
          </aside>
        )}

        {!isCaptaOneTv && sidebarMobile && (
          <div
            className="fixed inset-0 z-50 bg-black bg-opacity-40 lg:hidden"
            onClick={closeMobile}
          >
            <aside
              className="fixed top-0 left-0 h-full w-64 bg-gradient-to-r from-blue-600 via-blue-700 to-blue-900 text-white shadow-2xl z-50 flex flex-col p-6 space-y-4 border-r-4 border-white drop-shadow-[4px_0px_10px_rgba(255,255,255,0.12)] overflow-y-auto overflow-x-hidden overscroll-contain pb-6 [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.35)_transparent]"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="absolute top-4 right-4"
                onClick={closeMobile}
                type="button"
              >
                <ChevronLeft size={32} />
              </button>

              <SidebarContent
                aberto={true}
                closeMobile={closeMobile}
                openRelatorios={openRelatorios}
                setOpenRelatorios={setOpenRelatorios}
                openCadastro={openCadastro}
                setOpenCadastro={setOpenCadastro}
                openOperacoes={openOperacoes}
                setOpenOperacoes={setOpenOperacoes}
                openCaptaZap={openCaptaZap}
                setOpenCaptaZap={setOpenCaptaZap}
                openDashboards={openDashboards}
                setOpenDashboards={setOpenDashboards}
                openConfiguracoes={openConfiguracoes}
                setOpenConfiguracoes={setOpenConfiguracoes}
                perms={perms}
                empresaAtivaEfetiva={empresaAtivaEfetiva}
              />
            </aside>
          </div>
        )}

        {!isCaptaOneTv && !sidebarMobile && (
          <button
            onClick={() => setSidebarMobile(true)}
            className="fixed left-4 top-4 z-50 bg-blue-800 text-white rounded-full p-2 shadow-lg lg:hidden"
            aria-label="Abrir menu"
            type="button"
          >
            <MenuIcon size={28} />
          </button>
        )}

        <main
          className={`flex-1 bg-transparent flex flex-col min-h-0 transition-all duration-300 ${
            isCaptaOneTv
              ? "overflow-hidden lg:ml-0"
              : menuAberto
                ? "overflow-x-auto lg:ml-64"
                : "overflow-x-auto lg:ml-16"
          }`}
        >
          <div className="flex-1 min-h-0 flex flex-col">
            <Suspense
              fallback={<div className="p-4 text-white">Carregando...</div>}
            >
              <AppRoutes
                perms={perms}
                pushNotification={pushNotification}
                recebimentos={recebimentos}
                captadores={captadores}
                leads={leads}
                usuario={usuario}
                empresaAtivaEfetiva={empresaAtivaEfetiva}
              />
            </Suspense>
          </div>

          {!isCaptaOneTv && (
            <>
              <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                closeOnClick
                pauseOnHover
                draggable
                newestOnTop
                toastClassName="bg-red-600 text-white font-semibold shadow-md rounded-xl px-6 py-3"
                containerId="fluxo-caixa"
              />
              <CaptaChatWidget />
            </>
          )}
        </main>
      </div>
    </Layout>
  );
}
